import type { ReorderItem } from './imageSlotReorder';

/**
 * 商品画像枠の「枠モデル」(Amazon / マニュアル共通)。
 *
 * 各ページ (Amazon / マニュアル) は自分のデータからこのモデルを組み立て、共通コンポーネント
 * ProductImageSlotsSection はこのモデルだけを見て描画する。
 * サーバー由来の反映待ち (pending) と画面内の処理中 (localOp) は同時に立ち得るため分けて持つ。
 * 計画: docs/requirements/product-detail-page-consolidation-plan.md 4.1
 */
export type ProductImageSlot = {
  /** Amazon: main / pt01..pt08、マニュアル: main / sub-0..sub-7 */
  key: string;
  /** メイン画像 / 画像 1 ... (ツールチップ・名前編集・alt の既定に使う) */
  label: string;
  /** カード見出しに label の代わりに出す文言 (マニュアル: 名前があれば名前)。省略時は label */
  headerLabel?: string;
  /** 画像の alt (マニュアルのメイン画像は商品名)。省略時は label */
  imageAlt?: string;
  isMain: boolean;
  /** 正本の画像 (Amazon: カタログ URL / マニュアル: DB の URL)。無ければ空き枠 */
  url: string | null;
  /** 画像の名前 (画像内容の説明。画像と一緒に動く) */
  name?: string | null;
  /** 保存時にそのまま戻す付随データ (マニュアルの width / height / original_path)。並べ替えで要素が動いても一緒に運ぶ */
  meta?: Record<string, unknown>;
  /** 形式のバッジ (マニュアルのベクター原本 = 'SVG')。押すと原本をダウンロード (onDownloadOriginal) */
  formatBadge?: string;
  /** サーバー由来の反映待ち (Amazon)。送信した画像と送信時刻 */
  pending?: { url: string; uploadedAt: string } | null;
  /** 画面内の処理中 (Amazon)。差替は送信中の画像 URL を持つ */
  localOp?: { kind: 'replacing'; url: string } | { kind: 'deleting' } | null;
  /** Amazon のメイン画像の解像度表示 */
  resolution?: { width: number; height: number } | null;
  /** 並べ替え対象外 (Amazon のメイン)。並べ替え中に「固定」として元の位置に表示する */
  locked?: boolean;
};

/** 操作の可否。ページ・モードごとにアダプタが宣言する (計画 2.5 の表) */
export type SlotCapabilities = {
  /** 画像クリックで選択済み素材へ */
  select: boolean;
  /** フォルダ保存ボタン */
  save: boolean;
  /** 差替 (SlotActionButtons) */
  replace: boolean;
  /** 削除 */
  delete: boolean;
  /** 名前編集 (ImageNameButton + 3点メニュー) */
  editName: boolean;
  /** 空き枠へのファイルドロップ */
  fileDrop: boolean;
  /** 空き枠クリック */
  emptyClick: boolean;
  /** 並べ替えボタンを出す (無効にする理由は reorder.disabledReason で別に渡す) */
  reorder: boolean;
};

/** 描画が見る導出済みの表示状態 (計画 2.4 の規則をそのまま写す) */
export type SlotView = {
  /** 表示する画像: 差替処理中の送信 URL → 反映待ちの送信 URL → 正本 URL */
  shownUrl: string | null;
  isEmpty: boolean;
  /** 正本 URL があり、反映待ちでも処理中でもない (画像クリックで選択できる) */
  selectable: boolean;
  /** 正本 URL があり、反映待ちでない (フォルダ保存ボタンを出す) */
  showSaveButtons: boolean;
  showDeletingOverlay: boolean;
  showReplacingOverlay: boolean;
  /** 反映待ちがあり、差替処理中でない (反映待ちの帯を出す) */
  showPendingBar: boolean;
  /** 反映待ちまたは処理中 (並べ替え開始の判定に使う) */
  busy: boolean;
};

export function deriveSlotView(slot: ProductImageSlot): SlotView {
  const replacing = slot.localOp?.kind === 'replacing' ? slot.localOp : null;
  const deleting = slot.localOp?.kind === 'deleting';
  const pending = slot.pending ?? null;
  const shownUrl = replacing?.url ?? pending?.url ?? slot.url ?? null;
  const hasUrl = !!slot.url;
  return {
    shownUrl,
    isEmpty: !shownUrl,
    selectable: hasUrl && !pending && !slot.localOp,
    showSaveButtons: hasUrl && !pending,
    showDeletingOverlay: deleting,
    showReplacingOverlay: !!replacing,
    showPendingBar: !!pending && !replacing,
    busy: !!pending || !!slot.localOp,
  };
}

/** 並べ替え用の要素へ (key は「元々あった枠」として要素と一緒に移動する) */
export function toReorderItem(slot: ProductImageSlot): ReorderItem {
  return { key: slot.key, url: slot.url, name: slot.name ?? null, meta: slot.meta };
}
